"use client";

import { useMemo } from "react";

import { useCurrencies } from "@/features/currencies/hooks/useCurrencies";
import type { Currency } from "@/features/currencies/types";
import { useUserBalance } from "@/features/wallet/hooks/useUserBalance";
import { formatTokenAmount as formatWalletBalance } from "@/features/wallet/utils";

/**
 * Currency and balance resolution for a transaction review — Phase 06, hoisted
 * in Phase 07a.
 *
 * These three were written for the asset page's five money paths and lived
 * inside `NftFinanceSection`. The drop purchase flow needs exactly the same
 * resolution — a network decides which currencies exist, a currency decides
 * which wallet asset is the balance — and re-deriving it there would put two
 * different answers to "what is this user's balance" in the product. They are
 * moved here verbatim, not rewritten; `NftFinanceSection` imports them and its
 * behaviour is unchanged.
 */

export type CurrencyState = {
  hasNetwork: boolean;
  currencies: Currency[];
  isLoading: boolean;
  isError: boolean;
};

/**
 * The item's own network decides which currencies exist. `enabled` matters:
 * `getCurrencies()` defaults `network_id` to 1, so firing it without a resolved
 * network would silently offer Ethereum's currencies for an item on some other
 * chain.
 */
export function useNetworkCurrencies(networkId: number | undefined): CurrencyState {
  const hasNetwork = Number.isFinite(Number(networkId)) && Number(networkId) > 0;
  const query = useCurrencies({ network_id: Number(networkId) }, { enabled: hasNetwork });
  // Memoised: consumers depend on this array in an effect, and a fresh `[]`
  // on every render would re-run it on every render.
  const currencies = useMemo<Currency[]>(() => query.data?.data?.currencies ?? [], [query.data]);
  return { hasNetwork, currencies, isLoading: query.isLoading, isError: query.isError };
}

export type ResolvedBalance = {
  balance: { text: string; symbol: string } | null;
  /** Why there is no balance. Empty when there is one. */
  reason: string;
  isLoading: boolean;
  rawBalance: string | null;
};

/** The viewer's balance in one specific currency, matched by contract address. */
export function useCurrencyBalance(
  currency: Currency | undefined,
  lang: "fa" | "en",
): ResolvedBalance {
  const { wallets, isLoading } = useUserBalance();
  const isFa = lang === "fa";

  if (!currency) {
    return {
      balance: null,
      reason: isFa
        ? "پس از انتخاب ارز، موجودی شما در همان ارز نمایش داده می‌شود."
        : "Your balance appears once the currency is resolved.",
      isLoading,
      rawBalance: null,
    };
  }

  const target = String(currency.contract_address ?? "").toLowerCase();
  for (const wallet of wallets) {
    for (const asset of wallet.assets) {
      if (String(asset.contract_address ?? "").toLowerCase() === target) {
        return {
          balance: {
            // The product's single balance conversion. Its decimals are
            // inferred from the symbol because the balance endpoint does not
            // state them — a pre-existing contract (features/wallet/utils.ts),
            // not something introduced here.
            text: formatWalletBalance(asset.balance, asset.symbol, lang),
            symbol: (asset.symbol || currency.symbol || "").toUpperCase(),
          },
          reason: "",
          isLoading,
          rawBalance: asset.balance,
        };
      }
    }
  }

  return {
    balance: null,
    reason: isLoading
      ? isFa
        ? "در حال دریافت موجودی کیف پول..."
        : "Loading your wallet balance…"
      : isFa
        ? "این ارز در موجودی کیف‌های شما دیده نمی‌شود، بنابراین موجودی قابل نمایش نیست."
        : "This currency does not appear in your wallets, so there is no balance to show.",
    isLoading,
    rawBalance: null,
  };
}

/** Null when a currency can genuinely be resolved; otherwise the stated reason. */
export function currencyStateReason(state: CurrencyState, isFa: boolean): string | null {
  if (!state.hasNetwork) {
    return isFa
      ? "شبکه‌ی این آیتم مشخص نیست، بنابراین ارز معتبری برای این تراکنش وجود ندارد."
      : "This item's network is unknown, so no valid currency can be resolved for this transaction.";
  }
  if (state.isLoading) {
    return isFa ? "در حال دریافت ارزهای این شبکه..." : "Loading this network's currencies…";
  }
  if (state.isError) {
    return isFa ? "دریافت ارزهای این شبکه ناموفق بود." : "This network's currencies could not be loaded.";
  }
  if (state.currencies.length === 0) {
    return isFa
      ? "برای شبکه‌ی این آیتم ارزی تعریف نشده است."
      : "No currency is defined for this item's network.";
  }
  return null;
}
